import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import JsonView from 'react18-json-view';
import { QuestionOutlineIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import 'react18-json-view/src/style.css';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { getManualVerificationSelectedSessionProviderData } from '../../../../redux/manualVerification/providerDetails/actions';
import { getSelectedManualVerificationSessionProviderData } from '../../../../redux/manualVerification/providerDetails/selectors';
import {
  resetSelectedManualVerificationSessionData,
  setSelectRequestScreenSearchQuery,
} from '../../../../redux/manualVerification/sessionsDetails';
// import { resetSelectedManualVerificationSessionData, setSelectRequestScreenSearchQuery } from '../../../../redux/manualVerification/sessionsDetails'
import { fetchManualVerificationSessionData } from '../../../../redux/manualVerification/sessionsDetails/actions';
import { getSelectedManualVerificationSessionData } from '../../../../redux/manualVerification/sessionsDetails/selectors';
import {
  setSelectedSessionId,
  updateActiveStep as updateManualVerificationActiveStep,
} from '../../../../redux/manualVerification/stepperDetails';
import { getManualVerificationSelectedSessionId } from '../../../../redux/manualVerification/stepperDetails/selectors';
import { formatDate } from '../../../../utils/format-date';
import { isJSONParsable } from '../../../../utils/is-json-parsable';
import {
  arrayToObject,
  createParamValuesFromExistingData,
  extractParamsFromBody,
  extractParamsValuesFromURL,
  extractResponseSelectionValues,
  stringifyWithBooleanConversion,
} from '../../../../utils/manual-verification-session/create-param-values';
import {
  filterRequestBodyByRegex,
  filterRequestUrlByRegex,
  filterResponseBodyByRegex,
  sortByContentType,
} from '../../../../utils/manual-verification-session/filter-requests';
import {
  statusTagColors,
  statusUpperCaseToLowerCase,
} from '../../../../utils/manual-verification-session/manual-verification-constants';
import {
  retryManualVerificationWebhook,
  updateManualVerificationSession,
  updateSessionV2,
} from '../../../../utils/manual-verification-session/update-manual-verification-session-utils';
import {
  createManualVerificationClaimData,
  getManualVerifcationCapturedNetworkRequests,
} from '../../../../utils/manual-verification-session/utils';
import { NetworkRequest } from '../../../../utils/types';
import { getFirebaseIdToken } from '../../../../utils/utils';
import NetworkRequestsTable from './network-request-table.component';
import RequiredParametersTable from './required-parameters-table.component';
import { ManualVerificationWebhookDetailsModal } from './webhook-details-modal.component';

export const ManualVerificationSelectRequests = () => {
  const params = useParams();
  const navigate = useNavigate();

  const {
    isOpen: isWebhookDetailsModalOpen,
    onOpen: onWebhookDetailsModalOpen,
    onClose: onWebhookDetailsModalClose,
  } = useDisclosure();

  const {
    isOpen: isRejectClaimAlertOpen,
    onOpen: onOpenRejectClaimAlert,
    onClose: onCloseRejectClaimAlert,
  } = useDisclosure();

  const {
    isOpen: isCreateClaimModalOpen,
    onOpen: onOpenCreateClaimModal,
    onClose: onCloseCreateClaimModal,
  } = useDisclosure();

  const rejectClaimCancelRef = useRef(null);

  const sessionId = useAppSelector(getManualVerificationSelectedSessionId);
  const [decryptedData, setDecryptedData] = useState<NetworkRequest[]>([]);
  const [filteredNetworkRequestStore, setFilteredNetworkRequestStore] = useState<
    NetworkRequest[]
  >([...decryptedData]);
  const [searchQueryInput, setSearchQueryInput] = useState('');
  const [webhookRetryBtnLoading, setWebhookRetryBtnLoading] = useState<boolean>(false);
  const [claimData, setClaimData] = useState<any>({});
  const [generatedParamValues, setGeneratedParamValues] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const selectedSessionData = useAppSelector(getSelectedManualVerificationSessionData);
  const httpProviderId = selectedSessionData?.httpProviderId;
  const selectedManualVerificationSessionProviderData = useAppSelector(
    getSelectedManualVerificationSessionProviderData
  );
  const toast = useToast();

  useEffect(() => {
    getCapturedRequests();
  }, []);

  useEffect(() => {
    dispatch(fetchManualVerificationSessionData({ sessionId }));
  }, []);

  useEffect(() => {
    if (httpProviderId) {
      dispatch(getManualVerificationSelectedSessionProviderData({ httpProviderId }));
    }
  }, [httpProviderId]);

  async function getCapturedRequests() {
    try {
      const result = await getManualVerifcationCapturedNetworkRequests(sessionId);

      if (!result?.isSuccess) {
        return toast({
          title: 'Error',
          description: 'Unable to fetch network requests from db',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }

      let decrypted = result?.data;

      for (let i = 0; i < decrypted?.length; i++) {
        const data = decrypted[i];
        decrypted[i].contentType = isJSONParsable(data?.responseBody ?? '')
          ? 'application/json'
          : 'text/html';
      }

      decrypted = sortByContentType(decrypted);
      setDecryptedData(decrypted);
      setFilteredNetworkRequestStore(decrypted);
    } catch (error) {
      console.error('Error getting network requests from db:', error);
    }
  }

  function handleGoBack() {
    dispatch(setSelectedSessionId(''));
    dispatch(setSelectRequestScreenSearchQuery(''));
    dispatch(resetSelectedManualVerificationSessionData());

    if (params?.sessionId?.length) {
      navigate(-1);
    } else {
      dispatch(updateManualVerificationActiveStep(2));
    }
  }

  function handleSearch(searchString: string): void {
    const lowerCaseSearchString = searchString.toLowerCase();

    const filteredRequests = decryptedData.filter((networkReq: NetworkRequest) => {
      const urlMatch = networkReq.url.toLowerCase().includes(lowerCaseSearchString);
      const responseBodyMatch = networkReq.responseBody
        ? networkReq.responseBody.toLowerCase().includes(lowerCaseSearchString)
        : false;

      return urlMatch || responseBodyMatch;
    });

    setSearchQueryInput(searchString);
    setFilteredNetworkRequestStore(filteredRequests);
  }

  async function publishClaimHandler(status: string, withParamsValues: boolean = false) {
    setLoading(true);
    try {
      if (withParamsValues && !Object.keys(generatedParamValues)?.length) {
        return toast({
          title: 'Error',
          description: 'Unable to find URL',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }

      const accessToken = (await getFirebaseIdToken()) ?? '';
      let message: any = '';
      if (withParamsValues) {
        message = await updateManualVerificationSession(
          accessToken,
          sessionId,
          status,
          generatedParamValues
        );
        await updateSessionV2(sessionId, 'PROOF_MANUAL_VERIFICATION_SUCCESS');
      } else {
        message = await updateManualVerificationSession(accessToken, sessionId, status);
      }

      if (!message.isSuccess) {
        toast({
          title:
            status === 'VERIFIED'
              ? 'Claim Verification Failed'
              : 'Claim Rejection Failed',
          description: message.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title:
            status === 'VERIFIED'
              ? 'Claim Verification Successfull'
              : 'Claim Rejection Successfull',
          description: message.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });

        handleGoBack();
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'verification Failed',
        description: 'Unknown Error while verifying',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleRetryWebhook() {
    try {
      setWebhookRetryBtnLoading(true);
      const templateURL: string =
        selectedManualVerificationSessionProviderData?.url ?? '';
      const templateBody: string =
        selectedManualVerificationSessionProviderData?.bodySniff.template ?? '';

      const { isSuccess, errorMessage, paramValues } = createParamValuesFromExistingData({
        templateURL,
        templateBody,
        requestsData: decryptedData,
        method: selectedManualVerificationSessionProviderData?.method,
        parameters: selectedSessionData?.parameters,
      });

      if (!isSuccess) {
        onWebhookDetailsModalClose();
        return toast({
          title: 'Error',
          description: errorMessage ?? 'Unable to find URL',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }

      const accessToken = (await getFirebaseIdToken()) ?? '';

      const message = await retryManualVerificationWebhook(
        accessToken,
        sessionId,
        paramValues
      );
      if (!message.isSuccess) {
        toast({
          title: 'Failed',
          description: message.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      } else {
        toast({
          title: 'Successfull',
          description: message.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Something went wrong while retrying',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      dispatch(fetchManualVerificationSessionData({ sessionId }));
      setWebhookRetryBtnLoading(false);
      onWebhookDetailsModalClose();
    }
  }

  async function generateClaimData() {
    setLoading(true);
    try {
      let paramValues = {};
      const templateURL: string =
        selectedManualVerificationSessionProviderData?.url ?? '';
      const templateBody: string =
        selectedManualVerificationSessionProviderData?.bodySniff.template ?? '';
      const firstResponseMatch: string =
        selectedManualVerificationSessionProviderData?.responseSelections?.[0]
          ?.responseMatch;

      const allMatchingRequests = decryptedData.filter((req) => {
        try {
          const reqBody = stringifyWithBooleanConversion(JSON.parse(req.requestBody));
          return (
            filterRequestUrlByRegex(req.url, templateURL) &&
            filterRequestBodyByRegex(reqBody, templateBody) &&
            req.method === selectedManualVerificationSessionProviderData?.method &&
            filterResponseBodyByRegex(req.responseBody, firstResponseMatch)
          );
        } catch (error) {
          return (
            filterRequestUrlByRegex(req.url, templateURL) &&
            filterRequestBodyByRegex(req.requestBody, templateBody) &&
            req.method === selectedManualVerificationSessionProviderData?.method &&
            filterResponseBodyByRegex(req.responseBody, firstResponseMatch)
          );
        }
      });

      if (!allMatchingRequests?.length) {
        return toast({
          title: 'Error',
          description: 'Unable to find URL',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }

      const filteredURL = allMatchingRequests[0];
      const extractedURLParams = extractParamsValuesFromURL(
        filteredURL?.url,
        templateURL
      );
      const extractedBodyParams = extractParamsFromBody(
        filteredURL?.requestBody,
        templateBody
      );
      const extractedParameterValues = arrayToObject(
        selectedSessionData?.parameters ?? []
      );
      const extractedResponseSelectionValues = extractResponseSelectionValues(
        filteredURL.responseBody,
        selectedManualVerificationSessionProviderData?.responseSelections
      );

      paramValues = {
        ...(extractedURLParams ?? {}),
        ...(extractedBodyParams ?? {}),
        ...(extractedParameterValues ?? {}),
        ...(extractedResponseSelectionValues ?? {}),
      };

      const result = await createManualVerificationClaimData(httpProviderId, paramValues);
      if (result?.isSuccess) {
        setClaimData(result?.data?.claimData ?? {});
        setGeneratedParamValues(paramValues);
        onOpenCreateClaimModal();
      }
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: 'Something went wrong',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }

  // async function handleRefreshAllData() {
  // 	setLoading(true)
  // 	await dispatch(fetchManualVerificationSessionData({ sessionId }))
  // 	await dispatch(getManualVerificationSelectedSessionProviderData({ httpProviderId }))
  // 	setLoading(false)
  // }

  return (
    <>
      <Flex direction="column" h="100%" margin={0} padding={0} w="full">
        <Flex p={0} flex={1} direction="column" overflow="scroll">
          <Box>
            <Flex gap={2} pr={3} pl={2}>
              <Flex flex={1} direction="column" gap={4} mb={5} mt={5}>
                <Flex alignItems="center" gap={8}>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Session ID
                    </Heading>
                    <Text color="gray.600" className="small-font" fontFamily="inter">
                      {selectedSessionData?.sessionId}
                    </Text>
                  </Box>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Provider
                    </Heading>
                    <Flex gap={2} alignItems="center">
                      <Text color="gray.600" className="small-font" fontFamily="inter">
                        {selectedManualVerificationSessionProviderData?.name}
                      </Text>
                      <Tooltip
                        whiteSpace="pre-line"
                        label={
                          selectedManualVerificationSessionProviderData?.httpProviderId
                        }
                      >
                        <Icon
                          as={QuestionOutlineIcon}
                          w={4}
                          h={4}
                          color="gray.600"
                          cursor="pointer"
                        />
                      </Tooltip>
                    </Flex>
                  </Box>
                </Flex>
                <Flex>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Page Url
                    </Heading>
                    <Text color="gray.600" className="small-font">
                      {selectedManualVerificationSessionProviderData?.url}
                    </Text>
                  </Box>
                </Flex>
                <Flex gap={10}>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Requested At
                    </Heading>
                    <Text color="gray.600" className="small-font">
                      {formatDate(selectedSessionData?.createdAt)}
                    </Text>
                  </Box>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Last Updated
                    </Heading>
                    <Text color="gray.600" className="small-font">
                      {formatDate(selectedSessionData?.updatedAt)}
                    </Text>
                  </Box>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Current Status
                    </Heading>
                    <Text>
                      <Tag
                        className="small-tag-font"
                        borderRadius={5}
                        fontWeight="bold"
                        colorScheme={statusTagColors?.[selectedSessionData?.status]}
                        px={4}
                        textTransform="capitalize"
                      >
                        {statusUpperCaseToLowerCase?.[selectedSessionData?.status] ?? '-'}
                      </Tag>
                    </Text>
                  </Box>
                  <Box>
                    <Heading className="x-small-font" fontWeight={700} mb="3px">
                      Webhook Status
                    </Heading>
                    <Text>
                      <Tag
                        className="small-tag-font"
                        borderRadius={5}
                        fontWeight="bold"
                        colorScheme={
                          statusTagColors?.[selectedSessionData?.webhooks?.[0]?.status]
                        }
                        px={3}
                        textTransform="capitalize"
                      >
                        {statusUpperCaseToLowerCase?.[
                          selectedSessionData?.webhooks?.[0]?.status
                        ] ?? '-'}
                      </Tag>
                    </Text>
                  </Box>
                </Flex>
                <Flex alignItems="center" gap={10} w="full">
                  {selectedSessionData?.webhooks?.length > 0 && (
                    <Button
                      size="sm"
                      colorScheme="blackAlpha"
                      variant="link"
                      color="black"
                      className="small-font"
                      lineHeight="24px"
                      px={2}
                      textDecoration="underline"
                      onClick={onWebhookDetailsModalOpen}
                    >
                      View Webhook Details
                    </Button>
                  )}
                </Flex>
              </Flex>
              <Flex w="40%" maxW="500px">
                <RequiredParametersTable handleSearch={handleSearch} />
              </Flex>
            </Flex>
          </Box>
          {((selectedSessionData?.id &&
            selectedManualVerificationSessionProviderData?.url?.length > 0 &&
            selectedManualVerificationSessionProviderData?.responseSelections?.length >
              0 &&
            selectedSessionData?.status !== 'VERIFIED') ||
            selectedSessionData?.status === 'PENDING') && (
            <Flex mb={7} mt={2} alignItems="center" justifyContent="center" gap={20}>
              {selectedSessionData?.status === 'PENDING' && (
                <Button
                  colorScheme="blackAlpha"
                  borderColor="black"
                  color="black"
                  _hover={{
                    transform: 'scale(1.05)',
                  }}
                  isLoading={loading}
                  onClick={onOpenRejectClaimAlert}
                  variant="outline"
                  className="small-font"
                  size="sm"
                >
                  Reject Claim
                </Button>
              )}
              {selectedManualVerificationSessionProviderData?.url?.length > 0 &&
                selectedManualVerificationSessionProviderData?.responseSelections
                  ?.length > 0 &&
                selectedSessionData?.status !== 'VERIFIED' && (
                  <Button
                    colorScheme="blackAlpha"
                    borderColor="black"
                    color="black"
                    _hover={{
                      transform: 'scale(1.05)',
                    }}
                    isLoading={loading}
                    onClick={generateClaimData}
                    variant="outline"
                    className="small-font"
                    size="sm"
                  >
                    Use Existing Provider Config To Create Claim
                  </Button>
                )}
            </Flex>
          )}

          <NetworkRequestsTable
            searchQueryInput={searchQueryInput}
            handleSearch={handleSearch}
            filteredNetworkRequestStore={filteredNetworkRequestStore}
          />
        </Flex>
        {isWebhookDetailsModalOpen && (
          <ManualVerificationWebhookDetailsModal
            isWebhookDetailsModalOpen={isWebhookDetailsModalOpen}
            onWebhookDetailsModalClose={onWebhookDetailsModalClose}
            webhookRetryBtnLoading={webhookRetryBtnLoading}
            handleRetryWebhook={handleRetryWebhook}
          />
        )}
        {isRejectClaimAlertOpen && (
          <AlertDialog
            isOpen={isRejectClaimAlertOpen}
            leastDestructiveRef={rejectClaimCancelRef}
            onClose={onCloseRejectClaimAlert}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogCloseButton />
                <AlertDialogHeader className="regular-font" fontWeight="bold">
                  Reject Claim
                </AlertDialogHeader>
                <AlertDialogBody>
                  Are you sure? You can&apos;t undo this action afterwards.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button
                    colorScheme="blackAlpha"
                    bgColor="black"
                    _hover={{
                      transform: 'scale(1.1)',
                    }}
                    isLoading={loading}
                    onClick={() => publishClaimHandler('REJECTED', false)}
                  >
                    Reject
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        )}
        {isCreateClaimModalOpen && (
          <Modal
            isOpen={isCreateClaimModalOpen}
            onClose={onCloseCreateClaimModal}
            size="3xl"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                <Heading>Preview Claim Data</Heading>
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Flex direction="column" gap={1} w="100%">
                  <Box
                    borderRadius={7}
                    bgColor="gray.50"
                    h="40vh"
                    overflow="scroll"
                    p={2}
                  >
                    <JsonView src={claimData} />
                  </Box>
                </Flex>
              </ModalBody>

              <ModalFooter>
                <Button
                  colorScheme="blackAlpha"
                  bgColor="black"
                  _hover={{
                    transform: 'scale(1.1)',
                  }}
                  isLoading={loading}
                  onClick={() => publishClaimHandler('VERIFIED', true)}
                >
                  Create Claim
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        )}
        <Box w="full" backgroundColor="gray.50" px={30} py={4}>
          <Flex w="full" justifyContent="space-between">
            <Button
              onClick={handleGoBack}
              backgroundColor="transparent"
              border="1px solid gray"
              borderRadius={10}
              fontWeight="normal"
              _hover={{
                fontWeight: 'bold',
                transform: 'scale(1.05)',
              }}
            >
              Back
            </Button>
          </Flex>
        </Box>
      </Flex>
    </>
  );
};
